/**
 * 入住后的「到家等待」：按专属形象的进度（GET /pets/{pet_id}/character，纯读）判断现在处在哪一段。
 * 依据 c84a D0742-2、D0812-1 与 6c2b 16:4x／16:5x：
 * - **只有完整才算到家**：state.pet_id 是这一只；当前形象 active 存在、pet_id 也是这一只、不是旧版本（outdated）；有中性站姿；
 *   后端声明的必要姿态集合**已知**，集合里每个姿态都 ready 且 active.assets 里有那张图。缺一样都不算到家。
 * - 必要姿态集合说不准（字段缺失、null、认不出）不能当成空集合：中性好了也停在「还在确认」。空数组 [] 才是「确定不需要」。
 * - 等待、失败、平台满额分开：失败照实说、不伪装排队；unknown 说「还在确认」，不说正在画。
 * - 读不到（会话、元数据、接口、pet_id 对不上）照实说读不到，不自动回家；**不设总时限、不自动退出**，只把读的频率放慢。
 * - 没画成带上原因码（中性形象取 candidate.reason ?? blocked_reason，姿态取没画成那一条的 reason），页面按 failedRouteOf 分出路（CREATE-PET-REGEN-01）。
 */
import { CharacterPoseValues, type CharacterReason, type CharacterState, type HouseholdBrief, type HouseholdPetBrief } from "@/shared/contracts";

/** 等了这么久以后，读的频率从 3 秒放慢到 10 秒（不退出、不跳转） */
export const SLOW_AFTER_MS = 2 * 60_000;
/** 连续读失败几次就照实说读不到 */
export const MAX_READ_ERRORS = 3;
export const POLL_MS = 3_000;
export const SLOW_POLL_MS = 10_000;
/** 到家前「TA 到家了」停留多久（减少动态时不停） */
export const ARRIVED_BEAT_MS = 900;

/** 在等画画名额：只认 platform_quota_wait（I 16:3x 冻结，状态 queued）。「有名额后继续」只有它成立 */
const QUOTA_REASONS = new Set(["platform_quota_wait"]);
/**
 * 名额满：budget_denied、daily_cap 在 A 的实现落地前是终态 failed、不会继续（web_character/model.py NO_RETRY_REASONS）。
 * 只说「名额满了、没能开始画」，**不做任何费用保证**（c84a D0912-2：必要姿态失败时中性和别的姿态可能已经成功结算，
 * 读口也没有「这一笔没发出、零预占」的事实字段）。
 */
const DAY_FULL_REASONS = new Set(["budget_denied", "daily_cap"]);
/** 结果不明（可能已经计费）：绝不重发，也不说正在画，只说还在确认（A 对照表，6c2b 16:5x） */
const UNKNOWN_REASONS = new Set(["unknown_result", "timeout", "unconfirmed"]);
/** 供应商没受理：后端会自动再试 1 次；不在生成类型的 CharacterReason 里，只按字符串比 */
const RETRY_REASON = "provider_error";
/** 现在还画不了（不论什么状态） */
const NOT_CONFIGURED = "not_configured";
const POSES: ReadonlySet<string> = new Set(CharacterPoseValues);

export type ArrivalGate = "loading" | "wait" | "skip";

export type ArrivalPhase =
  /** 还没读到 */
  | { kind: "checking" }
  /** 中性形象排队中 */
  | { kind: "finding" }
  /** 中性形象在等画画名额 */
  | { kind: "quotaWait"; stage: 0 | 1 }
  /** 中性形象生成中 */
  | { kind: "transferring" }
  /** 供应商没受理（provider_error）、还没到终态：后端会再试一次 */
  | { kind: "retrying" }
  /** 中性好了，必要姿态还在画；ready/total 只用来画小圆点；quotaWait＝有必要姿态在等名额 */
  | { kind: "bringing"; ready: number; total: number; quotaWait: boolean }
  /** 结果还没确认（unknown、必要姿态集合说不准、形象不完整或对不上、状态认不出）：接着读，不说正在画 */
  | { kind: "confirming"; stage: 1 | 2 }
  /** 完整了：自动到家 */
  | { kind: "arrived" }
  /** 没画成：scope＝中性形象（figure）还是某个必要姿态（poses）；dayFull＝原因是名额满（不代表费用）；reason＝原因码原样（只用来分出路，不给人看） */
  | { kind: "failed"; scope: "figure" | "poses"; dayFull: boolean; reason: string | null }
  /** 现在画不了：not_configured（不论状态），或 absent 且后端给了别的 blocked_reason */
  | { kind: "blocked"; notConfigured: boolean }
  /** 读不到：会话、元数据、接口失败，或回来的不是这一只 */
  | { kind: "unreachable" };

export interface ArrivalInput {
  gate: ArrivalGate;
  state: CharacterState | null | undefined;
  /** 这一页等的是哪一只；不给就无法核对，不会算到家 */
  petId?: string | null;
  /** 后端声明的必要姿态集合；null／不给＝说不准（不能当空集合） */
  requiredPoses?: readonly string[] | null;
  /** 进这一页以来过了多久（只用来放慢读的频率） */
  elapsedMs: number;
  /** 连续读失败的次数（读成功就归零） */
  readErrors: number;
}

/**
 * 必要姿态集合的读取函数（6c2b 16:5x；I 已批 required_poses: CharacterPose[] | null，生成物还没有，不依赖新类型）。
 * 是数组且每一项都认得才返回；缺字段、null、不是数组、有一项认不出 → null（说不准）。[] 是合法的「确定不需要额外姿态」。
 */
export function requiredPosesOf(state: CharacterState | null | undefined): readonly string[] | null {
  if (!state) return null;
  const raw: unknown = Reflect.get(state, "required_poses");
  if (!Array.isArray(raw)) return null;
  if (!raw.every((p) => typeof p === "string" && POSES.has(p))) return null;
  return raw as string[];
}

const isQuota = (reason: string | null | undefined) => reason != null && QUOTA_REASONS.has(reason);
const dayFull = (reason: string | null | undefined) => reason != null && DAY_FULL_REASONS.has(reason);

export function arrivalPhase({ gate, state, petId, requiredPoses, readErrors }: ArrivalInput): ArrivalPhase {
  if (gate === "loading") return { kind: "checking" };
  if (gate === "skip" || readErrors >= MAX_READ_ERRORS) return { kind: "unreachable" };
  if (!state) return { kind: "checking" };
  if (!petId || state.pet_id !== petId) return { kind: "unreachable" };

  // 原因码一律按字符串比（A 对照表）；认不出的码按状态走
  const reason: string | null = state.candidate?.reason ?? state.blocked_reason ?? null;
  const status: string = state.status;
  if (reason === NOT_CONFIGURED) return { kind: "blocked", notConfigured: true };
  if (status === "unknown" || (reason !== null && UNKNOWN_REASONS.has(reason))) return { kind: "confirming", stage: 1 };
  if (status === "failed") return { kind: "failed", scope: "figure", dayFull: dayFull(reason), reason };
  if ((status === "queued" || status === "running") && reason === RETRY_REASON) return { kind: "retrying" };
  if (status === "queued") return isQuota(reason) ? { kind: "quotaWait", stage: 0 } : { kind: "finding" };
  if (status === "running") return isQuota(reason) ? { kind: "quotaWait", stage: 1 } : { kind: "transferring" };
  if (status === "absent") return state.candidate === null && state.blocked_reason !== null ? { kind: "blocked", notConfigured: false } : { kind: "confirming", stage: 1 };
  if (status !== "ready") return { kind: "confirming", stage: 1 }; // 认不出的状态

  // ready：逐条核完整
  const active = state.active;
  const has = (pose: string) => Boolean(active?.assets.some((a) => a.pose === pose && a.url));
  if (!active || active.pet_id !== petId || active.outdated === true || !has("neutral_full")) return { kind: "confirming", stage: 1 };
  if (requiredPoses == null) return { kind: "confirming", stage: 2 };
  const required = requiredPoses.filter((p) => p !== "neutral_full"); // 中性站姿上面已核
  const progress = new Map((state.poses ?? []).map((p) => [p.pose as string, p]));
  const rows = required.map((p) => progress.get(p));
  // 结果不明的先认（可能已经计费，不说没画成也不说在画），再认失败
  if (rows.some((p) => p && (p.status === "unknown" || (p.reason !== null && UNKNOWN_REASONS.has(p.reason))))) return { kind: "confirming", stage: 2 };
  const failed = rows.find((p) => p?.status === "failed");
  if (failed) return { kind: "failed", scope: "poses", dayFull: dayFull(failed.reason), reason: failed.reason ?? null };
  const done = required.filter((p) => progress.get(p)?.status === "ready" && has(p));
  if (done.length === required.length) return { kind: "arrived" };
  const rest = required.filter((p) => !done.includes(p)).map((p) => progress.get(p));
  // 缺进度、ready 却没有图、认不出的状态：还在确认
  if (rest.some((p) => !p || (p.status !== "queued" && p.status !== "running"))) return { kind: "confirming", stage: 2 };
  return { kind: "bringing", ready: done.length, total: required.length, quotaWait: rest.some((p) => isQuota(p!.reason)) };
}

/**
 * 没画成以后给哪几条出路（CREATE-PET-REGEN-01，I 10-09 定）：按原因码分，不怪主人，也不把码给人看。
 * - "retry"：只说这次没画好、再试一次，**不叫人换照片**。画像服务回的图带背景（background_not_removed）不是主人的错——
 *   这个码 I108 进了契约，下面单写一支（CONTRACT-SYNC-I108），结果和“认不出的码”一样。其余认得、但不是照片问题的码也在这一支。
 *   photo_* 也在这一支（I 10-09 定）：后端 web_character/validate.py 那几条查的是画出来的证件照成品（头顶留白、头被裁、比例、尺寸），
 *   不是主人上传的照片，叫主人换照片等于甩锅。
 * - "tooLarge"：subject_too_large，再画一次、换一张照片两条路都给（再画一次在前），附一句温和的照片建议。
 * 名额满（dayFull）不走这里：页面不给再画一次，也不叫人换照片。
 */
export type FailedRoute = "retry" | "tooLarge";
const TOO_LARGE_REASON = "subject_too_large" satisfies CharacterReason;
/** 画像服务回的图带背景（I108 进契约）：不是主人的错，只给「再试一次」，不叫人换照片 */
const BACKGROUND_REASON = "background_not_removed" satisfies CharacterReason;

export function failedRouteOf(reason: string | null | undefined): FailedRoute {
  if (reason === TOO_LARGE_REASON) return "tooLarge";
  if (reason === BACKGROUND_REASON) return "retry";
  return "retry";
}

/**
 * 「换一张照片」去 TA 的形象页（/me/look?pet=<id>），那一页凭 ?pet= 认这一只：只认已经住进来、家已启用的宠物
 * （shared/session/householdContext 的 availablePets 同一个条件），不在里面就只显示“这里看不到了”。这里先按同一个条件在 /households 里找，
 * 找不到就不给这个入口；照片能不能换，另由形象页同一条规则（me/LookPage 的 canReplacePhoto）定。
 */
export function arrivingPetOf(list: readonly HouseholdBrief[] | null | undefined, petId: string | null | undefined): HouseholdPetBrief | null {
  if (!list || !petId) return null;
  for (const household of list) {
    if (!household.home_activated) continue;
    const pet = household.pets.find((p) => p.pet_id === petId && p.join_step === "moved_in");
    if (pet) return pet;
  }
  return null;
}

/** 三段提示里亮到第几段（0 找 TA、1 传输意识、2 带回样子、3 到家）；没有段落的分支返回 null */
export function stageOf(phase: ArrivalPhase): 0 | 1 | 2 | 3 | null {
  switch (phase.kind) {
    case "checking":
    case "finding":
      return 0;
    case "transferring":
    case "retrying":
      return 1;
    case "bringing":
      return 2;
    case "arrived":
      return 3;
    case "quotaWait":
    case "confirming":
      return phase.stage;
    default:
      return null;
  }
}

/** 还在等、要接着读的分支 */
export function isWaiting(phase: ArrivalPhase): boolean {
  return ["checking", "finding", "quotaWait", "transferring", "retrying", "bringing", "confirming"].includes(phase.kind);
}

/** 多久再读一次：等着时 3 秒，等了两分钟以后或在等名额时 10 秒；有结果、读不到（等主人点「再看一次」）就不自动读 */
export function pollInterval(phase: ArrivalPhase, elapsedMs: number): number | false {
  if (!isWaiting(phase)) return false;
  if (phase.kind === "quotaWait" || (phase.kind === "bringing" && phase.quotaWait)) return SLOW_POLL_MS;
  return elapsedMs >= SLOW_AFTER_MS ? SLOW_POLL_MS : POLL_MS;
}

/**
 * 等名额时「下一次检查：HH:mm」用的时刻（I 16:3x 冻结：CharacterState 新增可空 next_check_at，时区感知）。
 * 生成物还没有这个字段：按可选字段读。按看的人所在时区给 HH:mm；没有、格式不对、已经过了就返回 null，不显示。
 */
export function nextCheckTime(state: CharacterState | null | undefined, now: number): string | null {
  if (!state) return null;
  const raw: unknown = Reflect.get(state, "next_check_at");
  if (typeof raw !== "string" || !raw) return null;
  const at = new Date(raw);
  if (Number.isNaN(at.getTime()) || at.getTime() <= now) return null;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

const IN_FLIGHT: ReadonlySet<string> = new Set(["queued", "running", "unknown"]);

/**
 * 给小窝气泡用（6c2b 17:0x，替代原 arrivalStillOnTheWay）：只看首套，老宠物不能被误判。
 * - "on_the_way"：还没有形象、且顶层 queued／running／unknown（含等名额）；或首套形象（revision 1）已有、还有姿态 queued／running／unknown。
 * - "not_drawn"：还没有形象、且顶层 failed（含名额满、not_configured）。
 * - null：读不到、absent、主人自己重画过的（revision > 1，不归等待页管）、首套姿态都定下来了（ready 或 failed）、没有任何在进行中的任务。
 */
export function arrivalNotice(state: CharacterState | null | undefined): "on_the_way" | "not_drawn" | null {
  if (!state) return null;
  const status: string = state.status;
  if (!state.active) {
    if (IN_FLIGHT.has(status)) return "on_the_way";
    if (status === "failed") return "not_drawn";
    return null;
  }
  if (state.active.revision !== 1) return null;
  return (state.poses ?? []).some((p) => IN_FLIGHT.has(p.status)) ? "on_the_way" : null;
}
